import Database from "../utilities/database";
import StatefulSettings from "../controllers/StatefulSettings";
import {
  IPendingItem,
  ISettingsItem,
  IEventCustomDecisionResponse,
  ICustomSchedule,
  TaskBasketItem,
  PenaltyBasketItem,
  AuditMessage,
  IShowNotificationState,
} from "../ts/Definitions";
import {
  PendingUpdateType,
  AuditActionTypes,
  TraceLevel,
  AvailabilityDayType,
} from "../ts/enums";
import {
  MessageType,
  NotificationType,
  sendRequestProof,
} from "../controllers/messaging";
import { endOfToday, isBefore, isTomorrow, startOfTomorrow } from "date-fns";
import { formatValidDate } from "../utilities/dateUtils";
import Cookies from "../utilities/cookies";

export class MessageService {
  private database!: Database;
  public showMessage: (args: IShowNotificationState) => void = () => ({});

  public initialize(database: Database): void {
    this.database = database;
  }

  public setShowMessage(
    showMessage: (args: IShowNotificationState) => void
  ): void {
    this.showMessage = showMessage;
  }

  /**
   * Sends refresh request to member
   */
  public sendRefreshRequest(userSettings: StatefulSettings): boolean {
    userSettings.updatePendingItems({
      updateType: PendingUpdateType.refresh,
    });
    this.showMessage({
      message:
        "Refresh sent to member to refresh their task list. Await their reload.",
      persist: true,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          "Task List Refresh",
          "Admin Added",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends add request for items to user session
   */
  public sendAddRequest(
    proposedItems: IPendingItem[],
    userSettings: StatefulSettings
  ): boolean {
    let pendingItems = userSettings.getPendingState();
    pendingItems = pendingItems.filter(
      (p) => !p.date || !isNaN(Date.parse(p.date))
    );
    let updatedTomorrowPreview = false;
    proposedItems.forEach((proposedItem) => {
      if (!proposedItem.item) return;
      if (
        proposedItem.itemAdditionOptions &&
        proposedItem.itemAdditionOptions.isPillory
      ) {
        void this.database.auditUser(
          [
            new AuditMessage(
              AuditActionTypes.Session,
              proposedItem.item.name,
              "Item sent to pillory for user",
              TraceLevel.info
            ),
          ],
          userSettings.getControl()
        );
        userSettings.updatePilloryTopicFromPendingItem(proposedItem);
      } else if (
        pendingItems.find(
          (i) =>
            i.item &&
            i.item.key === proposedItem.item?.key &&
            i.updateType === PendingUpdateType.add &&
            (!proposedItem.date ||
              isBefore(new Date(proposedItem.date), endOfToday()))
        )
      ) {
        void this.database.auditUser(
          [
            new AuditMessage(
              AuditActionTypes.Alert,
              proposedItem.item.name,
              "Item already in member task list",
              TraceLevel.warning
            ),
          ],
          userSettings.getControl()
        );
        return false;
      } else {
        if (
          userSettings.isTomorrowAPreviewDay() &&
          proposedItem.date &&
          isTomorrow(new Date(proposedItem.date))
        )
          updatedTomorrowPreview = true;
        userSettings.updatePendingItems(proposedItem);
        void this.database.auditUser(
          [
            new AuditMessage(
              AuditActionTypes.Task,
              proposedItem.item.name,
              "Queued up by Owner for " +
              (proposedItem.date ? proposedItem.date : "now"),
              TraceLevel.info
            ),
          ],
          userSettings.getControl()
        );
      }
    });
    this.showMessage({
      message: "Requested items sent",
      persist: true,
      notificationType: NotificationType.ScheduleInfo,
    });
    if (updatedTomorrowPreview) {
      userSettings.updatePreviewAndResend(
        proposedItems.filter((i) => i.updateType === PendingUpdateType.add),
        "Owner added task to member session"
      );
    }
    return true;
  }

  /**
   * Sends remove request for item from user session
   */
  public sendRemoveRequest(
    item: ISettingsItem,
    userSettings: StatefulSettings
  ): boolean {
    if (!item) return false;
    const pendingItems = userSettings.getPendingState();
    if (
      pendingItems.find(
        (i) =>
          i.item &&
          i.item.key === item.key &&
          i.updateType === PendingUpdateType.remove
      )
    ) {
      this.showMessage({
        message: `${item.name} already pending for task list.`,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.database.auditUser(
        [
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Remove for ${item.name} already pending for task list.`,
            TraceLevel.warning
          ),
        ],
        userSettings.getControl()
      );
      return false;
    }
    userSettings.updatePendingItems({
      item,
      updateType: PendingUpdateType.remove,
    });
    this.showMessage({
      message: `${item.name} sent to ${userSettings.getControl().username
        } for task list removal. Await their reload.`,
      persist: true,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          "Admin Removed",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends schedule change request
   */
  public sendScheduleChange(
    item: ICustomSchedule,
    date: string,
    userSettings: StatefulSettings
  ): boolean {
    const pendingItems = userSettings.getPendingState();
    const idx = pendingItems.findIndex(
      (i) =>
        i.updateType === PendingUpdateType.scheduleChange && i.date === date
    );
    if (idx > -1) {
      pendingItems[idx] = {
        date,
        customScheduleKey: item.key,
        updateType: PendingUpdateType.scheduleChange,
      };
    } else {
      userSettings.updatePendingItems({
        date,
        customScheduleKey: item.key,
        updateType: PendingUpdateType.scheduleChange,
      });
    }
    this.showMessage({
      message: `${item.name} set for ${date} for  ${userSettings.getControl().username
        }.`,
      persist: false,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          `Custom Schedule ${item.name} Set for ${date}`,
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    if (isTomorrow(new Date(date))) userSettings.createPreview(true);
    return true;
  }

  /**
   * Sends event release request
   */
  public sendEventRelease(
    item: ISettingsItem,
    userSettings: StatefulSettings
  ): boolean {
    const pendingItems = userSettings.getPendingState();
    if (
      pendingItems.find(
        (i) =>
          i.item &&
          i.item.key === item.key &&
          i.updateType === PendingUpdateType.permRelease
      )
    ) {
      this.showMessage({
        message: `Permission Release for ${item.name} already pending for task list.`,
        persist: false,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.database.auditUser(
        [
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `${item.name} already pending for task list.`,
            TraceLevel.warning
          ),
        ],
        userSettings.getControl()
      );
      return false;
    }
    userSettings.updatePendingItems({
      item,
      fireImmediate: true,
      updateType: PendingUpdateType.permRelease,
    });
    this.showMessage({
      message: `${item.name} sent to ${userSettings.getControl().username
        } for immediate release. Await their reload.`,
      persist: false,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          "Admin Released",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends permission change request
   */
  public sendPermissionChange(
    item: IEventCustomDecisionResponse,
    userSettings: StatefulSettings
  ): boolean {
    const pendingItems = userSettings.getPendingState();
    if (
      pendingItems.find(
        (i) =>
          i.item &&
          i.item.key === item.itemKey &&
          i.updateType === PendingUpdateType.permissionChange
      )
    ) {
      this.showMessage({
        message: "Permission Change for item already pending for task list.",
        persist: false,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.database.auditUser(
        [
          new AuditMessage(
            AuditActionTypes.Session,
            item.itemKey,
            `Permission Change for ${item.itemKey} already pending for task list.`,
            TraceLevel.warning
          ),
        ],
        userSettings.getControl()
      );
      return false;
    }
    userSettings.updatePendingItems(
      {
        fireImmediate: true,
        updateType: PendingUpdateType.permissionChange,
        customDecisionResponse: item,
      },
      false
    );
    this.showMessage({
      message: `Change sent to ${userSettings.getControl().username
        }. Await their reload.`,
      persist: false,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.itemKey,
          "Admin Permission Change",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends task change request
   */
  public sendTaskChange(
    item: TaskBasketItem | PenaltyBasketItem,
    userSettings: StatefulSettings
  ): boolean {
    if (!item) return false;
    const pendingItems = userSettings.getPendingState();
    if (
      pendingItems.find(
        (i) =>
          i.item &&
          i.item.key === item.key &&
          i.updateType === PendingUpdateType.remove
      )
    ) {
      this.showMessage({
        message: `${item.name} change already pending for task list.`,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.database.auditUser(
        [
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Change for ${item.name} already pending for task list.`,
            TraceLevel.warning
          ),
        ],
        userSettings.getControl()
      );
      return false;
    }

    userSettings.updatePendingItems(
      {
        item,
        itemAdditionOptions: item as any,
        updateType: PendingUpdateType.update,
      },
      false
    );
    this.showMessage({
      message: `${item.name} sent to ${userSettings.getControl().username
        } for update. Await their reload.`,
      persist: true,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          "Admin Updated",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends request for proof
   */
  public sendRequestProof(
    item: TaskBasketItem | PenaltyBasketItem,
    userSettings: StatefulSettings,
    loggedOnUser: Cookies
  ): boolean {
    const message = sendRequestProof(item, userSettings, loggedOnUser);
    this.showMessage({
      message,
      notificationType: NotificationType.MessageSent,
    });

    if (!userSettings.canHandleDiscordProofCalls()) return true;
    const pendingItems = userSettings.getPendingState();
    if (
      pendingItems.find(
        (i) =>
          i.item &&
          i.item.key === item.key &&
          i.updateType === PendingUpdateType.proofRequest
      )
    ) {
      this.showMessage({
        message: "Proof Request for item already pending for task list.",
        persist: false,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.database.auditUser(
        [
          new AuditMessage(
            AuditActionTypes.Session,
            item.name,
            `Proof Request for ${item.name} already pending for task list.`,
            TraceLevel.warning
          ),
        ],
        userSettings.getControl()
      );
      return false;
    }
    userSettings.updatePendingItems(
      {
        fireImmediate: true,
        updateType: PendingUpdateType.proofRequest,
        item,
        itemAdditionOptions: { reason: message },
      },
      false
    );
    this.showMessage({
      message: `Request sent to ${userSettings.getControl().username}.`,
      persist: false,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          item.name,
          "Admin Request Proof",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Approves a request from member
   */
  public approveRequest(
    userid: string,
    item: ISettingsItem,
    messageType: MessageType | undefined,
    getSettings: (userid: string, callback: (settings: StatefulSettings) => void) => void,
    sendRemoveRequest: (item: ISettingsItem, settings: StatefulSettings) => boolean
  ): void {
    getSettings(userid, (editorSettings) => {
      const itemMatch = editorSettings.getCurrentItem(item);
      if (!itemMatch) return;
      switch (messageType) {
        case MessageType.RequestedDeleteTaskByMember: {
          const basketItemDetails = editorSettings
            .getBasketItems(true)
            .pendingItems.find((i) => i.key === item.key) as
            | TaskBasketItem
            | PenaltyBasketItem;
          sendRemoveRequest(basketItemDetails, editorSettings);
          itemMatch.hidden = true;
          editorSettings.updateSettingsItem(itemMatch);
          return;
        }
        case MessageType.RequestedTaskByMember: {
          const basketItemDetails = editorSettings
            .getBasketItems(true)
            .pendingItems.find((i) => i.key === item.key) as
            | TaskBasketItem
            | PenaltyBasketItem;
          sendRemoveRequest(basketItemDetails, editorSettings);
          itemMatch.hidden = false;
          editorSettings.updateSettingsItem(itemMatch);
          return;
        }
        case MessageType.RequestedRemoveTaskByMember: {
          const basketItemDetails = editorSettings
            .getBasketItems(true)
            .pendingItems.find((i) => i.key === item.key) as
            | TaskBasketItem
            | PenaltyBasketItem;
          sendRemoveRequest(basketItemDetails, editorSettings);
          return;
        }
        case MessageType.ReviewAvailability: {
          const basketItemDetails = editorSettings
            .getBasketItems(true)
            .pendingItems.find((i) => i.key === item.key) as
            | TaskBasketItem
            | PenaltyBasketItem;
          sendRemoveRequest(basketItemDetails, editorSettings);
          itemMatch.availabilityDayType = AvailabilityDayType.FreeDays;
          editorSettings.updateSettingsItem(itemMatch);
          return;
        }
        default:
          break;
      }
    });
  }

  /**
   * Postpones an item to tomorrow
   */
  public postponeItem(
    userid: string,
    key: string,
    getSettings: (userid: string, callback: (settings: StatefulSettings) => void) => void,
    sendRemoveRequest: (item: ISettingsItem, settings: StatefulSettings) => boolean,
    sendAddRequest: (items: IPendingItem[], settings: StatefulSettings) => boolean
  ): void {
    getSettings(userid, (editorSettings) => {
      const basketItemDetails = editorSettings
        .getBasketItems(true)
        .pendingItems.find((i) => i.key === key) as
        | TaskBasketItem
        | PenaltyBasketItem;
      sendRemoveRequest(basketItemDetails, editorSettings);
      sendAddRequest(
        [
          {
            updateType: PendingUpdateType.add,
            date: formatValidDate(startOfTomorrow(), "dd MMM yyyy"),
            item: basketItemDetails,
            fireImmediate: false,
            itemAdditionOptions: {
              reason: "Postponed from Yesterday",
              selectedCondition: basketItemDetails.selectedCondition,
              countType: basketItemDetails.countType,
              expectedCount: basketItemDetails.expectedCount,
              eventEndTrigger: basketItemDetails.endTriggerEvent,
              isPillory: false,
              isRoulette: basketItemDetails.isRoulette,
              ownerAdded: true,
            },
          },
        ],
        editorSettings
      );
    });
  }

  /**
   * Removes a pending schedule item (event/permission not yet fired) from the member's active schedule
   */
  public sendScheduleItemRemove(
    item: ISettingsItem,
    userSettings: StatefulSettings
  ): boolean {
    if (!item) return false;
    userSettings.updatePendingItems({
      item,
      updateType: PendingUpdateType.scheduleRemove,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Event,
          item.name,
          "Owner Removed Scheduled Item",
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Reschedules a pending schedule item to a new start time
   */
  public sendScheduleItemReschedule(
    item: ISettingsItem,
    newStartTime: Date,
    userSettings: StatefulSettings
  ): boolean {
    if (!item) return false;
    userSettings.updatePendingItems({
      item,
      date: newStartTime.toISOString(),
      updateType: PendingUpdateType.scheduleReschedule,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Event,
          item.name,
          `Owner Rescheduled Item to ${newStartTime.toISOString()}`,
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }

  /**
   * Sends a batch of schedule edit operations (removes, reschedules, adds) as pending items.
   * All existing pending items of the same types are first cleared to avoid duplicates.
   */
  public sendScheduleEdits(
    operations: IPendingItem[],
    userSettings: StatefulSettings
  ): boolean {
    if (!operations.length) return false;
    operations.forEach((op) => {
      userSettings.updatePendingItems(op);
    });
    this.showMessage({
      message: `Schedule changes sent to ${userSettings.getControl().username}. Await their reload.`,
      persist: true,
      notificationType: NotificationType.ScheduleInfo,
    });
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Event,
          "Schedule Edit",
          `Owner sent ${operations.length} schedule edit(s)`,
          TraceLevel.info
        ),
      ],
      userSettings.getControl()
    );
    return true;
  }
}
